import { artUrl, comingSoonText, isComingSoon, KIND_LABEL, label, type Game } from "./catalogue.ts";
import { h, link } from "./dom.ts";
import { downloadPath, gameActionLink, launch, playLink, type GameAction } from "./play.ts";
import { navigate } from "./router.ts";
import { cardBadge, coverUrl } from "./rows.ts";

/** Background for a game: its cover art (or `cover`) over the palette gradient (art can be missing). */
export function coverStyle(g: Game, variant: "full" | "thumb" | "blur" = "thumb"): Partial<CSSStyleDeclaration> {
  const art = coverUrl(g, variant);
  const gradient = `linear-gradient(160deg, ${safeColour(g.palette.top)}, ${safeColour(g.palette.bottom)})`;
  return { backgroundImage: art ? `url("${art}"), ${gradient}` : gradient };
}

/**
 * A card's cover, loaded when the card nears the viewport: the palette gradient now, the image (`data-bg`) once
 * `startLazyCovers` sees it. Home draws ~20 rows and browse every game, so eager covers were ~12 MB on a phone.
 */
export function lazyCover(g: Game, variant: "thumb" | "full" = "thumb"): { style: Partial<CSSStyleDeclaration>; "data-bg": string | null } {
  return { style: { backgroundImage: `linear-gradient(160deg, ${safeColour(g.palette.top)}, ${safeColour(g.palette.bottom)})` }, "data-bg": coverUrl(g, variant) };
}

/**
 * Watch the page for `[data-bg]` covers and set each as it comes within two screens of the viewport. A sideways shelf
 * clips its posters, so the observer can't see the ones past its edge: when a shelf's poster comes near, the next
 * page or so of that shelf loads with it (wave 12: rows looked like gradients because art loaded late). Call once.
 */
export function startLazyCovers(root: HTMLElement = document.body): void {
  const show = (el: HTMLElement) => {
    const bg = el.dataset.bg;
    el.removeAttribute("data-bg");
    if (bg) el.style.backgroundImage = `url("${bg}"), ${el.style.backgroundImage}`;
  };
  if (typeof IntersectionObserver === "undefined") {
    new MutationObserver(() => root.querySelectorAll<HTMLElement>("[data-bg]").forEach(show)).observe(root, { childList: true, subtree: true });
    return;
  }
  const AHEAD = 8; // posters past the one in view: a shelf page on desktop, two on a phone
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const el = e.target as HTMLElement;
        io.unobserve(el);
        show(el);
        const track = el.closest(".poster-track");
        if (!track) continue;
        const covers = [...track.querySelectorAll<HTMLElement>("[data-bg], .poster-art")];
        const i = covers.indexOf(el);
        for (const next of covers.slice(i + 1, i + 1 + AHEAD)) if (next.dataset.bg) (io.unobserve(next), show(next));
      }
    },
    { rootMargin: "200% 100%" },
  );
  const watch = () => root.querySelectorAll<HTMLElement>("[data-bg]").forEach((el) => io.observe(el));
  new MutationObserver(watch).observe(root, { childList: true, subtree: true });
  watch();
}

/**
 * A creator's name as players see it: first-party items are published under the internal "threedom" handle, which
 * shows as Banger.
 */
export function creatorName(name: string | null | undefined, handle = ""): string {
  const n = (name ?? "").trim() || handle;
  return n.toLowerCase() === FIRST_PARTY_HANDLE ? "Banger" : n;
}
const FIRST_PARTY_HANDLE = "threedom";

function safeColour(c: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(c) ? c : "#202027";
}

/** "Updated" or "New" on a card (wave 11), or nothing. */
export function newBadge(g: Game, boosted?: Set<string>, cls = "badge new"): HTMLElement | null {
  const b = cardBadge(g, boosted);
  return b ? h("span", { class: `${cls}${b === "Updated" ? " updated" : ""}` }, b) : null;
}

/**
 * A typographic cover (w12-art; the catalogue's `artKind: "type"`) carries the title on its poster (-thumb) only, so
 * cards and posters don't repeat it underneath. The full-size hero image has no type: heroes keep their title.
 */
export function typedCover(g: Pick<Game, "art">): boolean {
  return (g as { artKind?: unknown }).artKind === "type";
}

export function gameCard(g: Game, boosted?: Set<string>): HTMLElement {
  return h(
    "li",
    { class: "card" },
    link(
      `/g/${g.id}`,
      { class: "card-link", "aria-label": `${g.title} by ${creatorName(g.creator.name)}` },
      // the title shows under the art, or on the gradient when there's no art; a typographic poster is drawn whole
      // (contained, style.css `.card-art.typed`) and keeps the label too, so a title is never cropped or missing
      h("div", { class: typedCover(g) ? "card-art typed" : "card-art", ...lazyCover(g) }, coverUrl(g) ? null : h("span", { class: "card-art-title", "aria-hidden": "true" }, g.title)),
      h(
        "div",
        { class: "card-meta" },
        coverUrl(g) ? h("span", { class: "card-title" }, g.title) : null,
        h("span", { class: "card-sub" }, `${label(g.genre)} · ${creatorName(g.creator.name)}`),
        isComingSoon(g) ? h("span", { class: "card-soon" }, h("span", { class: "badge warn" }, comingSoonText(g))) : null,
        newBadge(g, boosted) ? h("span", { class: "card-soon" }, newBadge(g, boosted)) : null,
      ),
    ),
  );
}

/**
 * A game's title logo (`/art/<key>-title.png`) with the text title as the fallback: imported games have no logo
 * (catalogue/SHAPE.md), so a missing file turns back into the heading instead of a broken image.
 */
export function titleArt(g: Pick<Game, "art" | "title">, imgClass: string, fallback: () => HTMLElement, defer = false): HTMLElement {
  const src = artUrl(g.art, "title");
  if (!src) return fallback();
  // deferred: `data-src` until the carousel reaches this slide (see home.ts `reveal`)
  const img = h("img", { class: imgClass, src: defer ? null : src, "data-src": defer ? src : null, alt: g.title });
  img.addEventListener("error", () => img.replaceWith(fallback()), { once: true });
  return img;
}

export function gameGrid(games: Game[], empty = "Nothing here yet.", boosted?: Set<string>): HTMLElement {
  if (games.length === 0) return h("p", { class: "dim" }, empty);
  return h("ul", { class: "grid" }, games.map((g) => gameCard(g, boosted)));
}

export function chips(g: Game): HTMLElement {
  return h(
    "ul",
    { class: "chips", "aria-label": "Tags" },
    h("li", null, KIND_LABEL[g.kind]?.replace(/s$/, "") ?? g.kind),
    h("li", null, label(g.genre)),
    g.facets.multiplayer ? h("li", null, "Multiplayer") : null,
    g.facets.input.map((i) => h("li", null, label(i))),
  );
}

/**
 * A launcher action on a game ("Notify me in Banger", "Tip the creator"): a real thrdm://game/ link, upgraded to fall
 * back to the download page like Play.
 */
export function actionButton(g: Pick<Game, "id" | "title">, action: GameAction, text: string, cls = "btn ghost"): HTMLElement {
  const href = gameActionLink(g.id, action);
  return h(
    "a",
    {
      class: cls,
      href,
      "aria-label": `${text}: ${g.title} (opens the Banger app)`,
      onclick: (e: Event) => {
        e.preventDefault();
        launch(g.id, { link: href, onMissing: () => navigate(downloadPath(g.id, action)) });
      },
    },
    text,
  );
}

/**
 * "Play in browser": the game's own web page in a new tab, beside the app's Play button. Web games only (a native game
 * has no page to open), and nothing for a coming-soon game.
 */
export function browserPlayLink(g: Game, cls = "btn ghost"): HTMLElement | null {
  if (isComingSoon(g) || g.runtime !== "web" || !/^https:\/\//.test(g.url)) return null;
  return h("a", { class: cls, href: g.url, target: "_blank", rel: "noopener", "aria-label": `Play ${g.title} in your browser (opens its site in a new tab)` }, "Play in browser");
}

/**
 * Play, as a game's actions show it: the app's Play, with "Play in browser" beside it for web games. On a phone or
 * tablet (style.css, `.app-play`/`.web-play`) the app can't run, so "Play in browser" takes Play's place as the one
 * primary action. `webOnDesktop: false` keeps the browser link to phones (the home hero, which has Details beside it).
 */
export function playActions(g: Game, size: "big" | "small" = "big", webOnDesktop = true): HTMLElement[] {
  const web = browserPlayLink(g, `btn ghost web-play${size === "small" ? " small" : ""}${webOnDesktop ? "" : " phone-only"}`);
  const app = playButton(g, size);
  if (!web) return [app];
  app.classList.add("app-play");
  return [app, web];
}

/**
 * The Play button: a real thrdm:// link (works with JS off), upgraded to fall back to the download page. A coming-soon
 * game has nothing to play yet, so it gets its expected date and "Notify me in Banger" instead, everywhere.
 */
export function playButton(g: Game, size: "big" | "small" = "big"): HTMLElement {
  if (isComingSoon(g))
    return h(
      "span",
      { class: "coming-soon" },
      h("span", { class: "badge warn" }, comingSoonText(g)),
      actionButton(g, "notify", "Notify me in Banger", `btn${size === "small" ? " small" : ""}`),
    );
  return h(
    "a",
    {
      class: `btn-play ${size}`,
      href: playLink(g.id),
      "aria-label": `Play ${g.title} in the Banger app`,
      onclick: (e: Event) => {
        e.preventDefault();
        launch(g.id, { onMissing: () => navigate(downloadPath(g.id)) });
      },
    },
    h("span", { "aria-hidden": "true", class: "tri" }, "▶"),
    "Play",
  );
}
